import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { marketplaceListingService } from '../services/api';

import CloseListingModal from '../components/CloseListingModal';

interface Card {
  id: string;
  name: string;
  imageUrl?: string;
  cardNumber: string;
  rarity?: string;
  type?: string;
}

interface MarketplaceListing {
  id: string;
  cardId?: number;
  sellerId: number;
  isBundle?: boolean;
  bundleName?: string;
  bundleItems?: any;
  customCardName?: string;
  price: number;
  condition: string;
  quantity: number;
  description?: string;
  imageUrls: string;
  status: string;
  listingType: 'WTS' | 'WTB';
  card?: Card;
  seller: {
    id: number;
    displayName: string;
    profilePicture?: string;
    discordId?: string;
    averageRating?: number;
    totalReviews?: number;
  };
  externalLink?: string;
  closingPrice?: number;
  createdAt: string;
}

interface ListingChangeLog {
  id: number;
  changeType: string;
  oldValue: string;
  newValue: string;
  changedBy: number;
  createdAt: string;
}

const ListingDetailPage = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [listing, setListing] = useState<MarketplaceListing | null>(null);
  const [loading, setLoading] = useState(true);
  const [changeLogs, setChangeLogs] = useState<ListingChangeLog[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [sellerImages, setSellerImages] = useState<string[]>([]);
  const [isCloseModalOpen, setIsCloseModalOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);

  useEffect(() => {
    // Get current user from localStorage
    const user = localStorage.getItem('user');
    if (user) {
      try {
        const parsedUser = JSON.parse(user);
        setCurrentUser(parsedUser);
      } catch (e) {
        console.error('Failed to parse user from localStorage:', e);
        setCurrentUser(null);
      }
    } else {
      setCurrentUser(null);
    }
  }, []);

  useEffect(() => {
    if (id) {
      fetchListingDetails();
    }
  }, [id, currentUser]);

  const fetchListingDetails = async () => {
    try {
      setLoading(true);
      const response = await marketplaceListingService.getListingById(id!);
      setListing(response.data.data);

      // Parse seller's uploaded images
      try {
        const images = JSON.parse(response.data.data.imageUrls);
        setSellerImages(images);
      } catch {
        setSellerImages([]);
      }

      // Fetch change logs if user is admin
      if (currentUser && currentUser.role === 'admin') {
        try {
          const changesResponse = await marketplaceListingService.getListingChanges(id!);
          setChangeLogs(changesResponse.data.data || []);
        } catch (err) {
          console.error('Failed to fetch change logs:', err);
          setChangeLogs([]); // Set to empty array on error
        }
      } else {
        setChangeLogs([]);
      }

      setError(null);
    } catch (error: any) {
      console.error('Failed to fetch listing details:', error);
      setError(t('marketplaceListing.errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  const handleCloseListing = async (finalPrice: number | null, quantity?: number) => {
    if (!listing) return;

    try {
      setLoading(true);
      await marketplaceListingService.closeListing(listing.id, {
        closingPrice: finalPrice,
        closedQuantity: quantity
      });
      // Redirect to marketplace
      navigate('/marketplace');
    } catch (error: any) {
      console.error('Failed to close listing:', error);
      setError(error.response?.data?.message || 'Failed to close listing');
      setLoading(false); // Ensure loading is false on error
    } finally {
      // setLoading(false); // Handled above or on component unmount if navigating
      setIsCloseModalOpen(false);
    }
  };


  const handleCancelListing = async () => {
    if (!listing) return;
    if (!window.confirm(t('marketplaceListing.confirmCancel') || 'Are you sure you want to cancel this listing?')) {
      return;
    }

    try {
      setLoading(true);
      await marketplaceListingService.cancelListing(listing.id);
      // Refresh or redirect
      // fetchListingDetails(); // Ideally status becomes CANCELLED
      navigate('/marketplace'); // Redirect to marketplace on cancel is clearer
    } catch (error: any) {
      console.error('Failed to cancel listing:', error);
      setError(error.response?.data?.message || 'Failed to cancel listing');
      setLoading(false);
    }
  };


  const renderStars = (rating: number) => (
    <div className="flex items-center text-sm">
      {[1, 2, 3, 4, 5].map((star) => (
        <span key={star} className={`${star <= Math.round(rating) ? 'text-amber-500' : 'text-gray-300'}`}>
          ★
        </span>
      ))}
    </div>
  );

  const getTypeBackground = (type: string) => {
    switch (type) {
      case 'Avatar': return 'bg-red-500 text-white';
      case 'Magic': return 'bg-blue-500 text-white';
      case 'Construct': return 'bg-yellow-500 text-white';
      case 'Life': return 'bg-gray-500 text-white';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getRarityColor = (rarity: string) => {
    const rarityLower = rarity.toLowerCase();
    if (rarityLower === 'c' || rarityLower === 'common') return 'bg-gray-500 text-white';
    if (rarityLower === 'r' || rarityLower === 'rare') return 'bg-blue-500 text-white';
    if (rarityLower === 'sr' || rarityLower === 'super rare') return 'bg-purple-500 text-white';
    if (rarityLower === 'ur' || rarityLower === 'ultra rare') return 'bg-yellow-500 text-white';
    if (rarityLower === 'scr' || rarityLower === 'secret rare') {
      return 'bg-gradient-to-r from-red-500 via-yellow-500 via-green-500 via-blue-500 to-purple-500 text-white';
    }
    if (rarityLower === 'cbr') return 'bg-[#D2B48C] text-black';
    if (rarityLower === 'pr') return 'bg-[#F5F5F5] text-black border border-gray-300';
    return 'bg-gray-500 text-white';
  };



  const parseBundleItems = (raw: any): { name: string; quantity?: number; notes?: string }[] => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">{t('marketplaceListing.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !listing) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
        <button
          onClick={() => navigate('/marketplace')}
          className="mt-4 text-blue-600 hover:text-blue-700"
        >
          {t('marketplaceListing.back')}
        </button>
      </div>
    );
  }

  if (!listing) {
    return null;
  }

  const bundleItems = listing.isBundle ? parseBundleItems(listing.bundleItems) : [];
  const primaryImage = listing.card?.imageUrl || sellerImages[0];
  const listingTitle = listing.isBundle
    ? listing.bundleName || 'Card Bundle'
    : listing.card?.name || listing.customCardName || 'Listing';

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      {/* Back Button */}
      <button
        onClick={() => navigate('/marketplace')}
        className="text-blue-600 hover:text-blue-700 mb-6 flex items-center"
      >
        {t('marketplaceListing.back')}
      </button>

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      <div className="grid gap-8 grid-cols-1 max-w-4xl mx-auto">
        {/* Removed Seller Images Column */}

        {/* Details Column */}
        <div>
          <div className="bg-white rounded-lg shadow-lg p-6 relative">
            {/* Card Image Reference - Top Right */}
            <div className="absolute top-6 right-6 w-24 h-32 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg overflow-hidden shadow-md border-2 border-gray-200">
              {primaryImage ? (
                <img
                  src={primaryImage}
                  alt={listingTitle}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-2xl">🎴</div>
                  </div>
                </div>
              )}
            </div>

            {/* Card Name */}
            <h1 className="text-3xl font-bold mb-2 pr-28">{listingTitle}</h1>

            {/* Card or Bundle Info */}
            {listing.isBundle ? (
              <div className="flex items-center gap-3 mb-4 text-sm text-gray-600">
                <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded font-semibold">{t('marketplaceListing.bundle')}</span>
                <span>{t('marketplaceListing.items', { count: bundleItems.length })}</span>
              </div>
            ) : (
              <div className="flex items-center gap-3 mb-4 text-sm text-gray-600">
                {listing.card?.cardNumber && <span>#{listing.card.cardNumber}</span>}
                {listing.card?.rarity && (
                  <span className={`px-2 py-1 rounded font-medium ${getRarityColor(listing.card.rarity)}`}>
                    {listing.card.rarity}
                  </span>
                )}
                {listing.card?.type && (
                  <span className={`px-2 py-1 rounded font-medium ${getTypeBackground(listing.card.type)}`}>{listing.card.type}</span>
                )}
              </div>
            )}

            <div className="mb-6 pb-6 border-b border-gray-200">
              <div className={`text-4xl font-bold mb-2 ${listing.listingType?.toUpperCase() === 'WTB' ? 'text-green-600' : 'text-blue-600'}`}>
                {listing.price != null
                  ? `฿${listing.price.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
                  : t('marketplaceListing.offerPrice')}
              </div>
              {listing.listingType?.toUpperCase() === 'WTS' && (listing as any).shippingCost > 0 && (
                <div className="text-sm text-gray-600 mb-1">
                  + ฿{(listing as any).shippingCost.toLocaleString()} {t('marketplaceListing.shippingCost') || 'Shipping'}
                </div>
              )}
              <div className="text-sm text-gray-600 font-medium">
                {listing.isBundle
                  ? t('marketplaceListing.bundlePrice')
                  : `${listing.listingType?.toUpperCase() === 'WTB' ? t('marketplaceListing.budgetFor') : ''} ${t(`marketplaceListing.${listing.quantity === 1 ? 'copies' : 'copies_plural'}`, { count: listing.quantity })} ${listing.listingType?.toUpperCase() === 'WTB' ? t('marketplaceListing.wanted') : t('marketplaceListing.available')}`}
              </div>
              {listing.listingType?.toUpperCase() === 'WTB' && (
                <div className="mt-2 inline-block px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                  {t('marketplaceListing.wtb')}
                </div>
              )}
            </div>

            {listing.isBundle && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-700 mb-2">{t('marketplaceListing.bundleContents')}</h3>
                <div className="space-y-2">
                  {bundleItems.length > 0 ? (
                    bundleItems.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-gray-800">
                        <span className="text-gray-500">•</span>
                        <div className="flex-1">
                          <div className="font-medium">
                            {item.quantity ? `${item.quantity}x ` : ''}{item.name}
                          </div>
                          {item.notes && <div className="text-sm text-gray-600">{item.notes}</div>}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-600">{t('marketplaceListing.noBundleDetails')}</p>
                  )}
                </div>
              </div>
            )}



            {/* Description */}
            {listing.description && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-700 mb-2">{t('marketplaceListing.description')}</h3>
                <p className="text-gray-600 whitespace-pre-wrap">{listing.description}</p>
              </div>
            )}

            {/* Seller/Buyer Info */}
            <div className="mb-6 pb-6 border-b border-gray-200">
              <h3 className="text-sm font-medium text-gray-700 mb-2">
                {listing.listingType?.toUpperCase() === 'WTB' ? t('marketplaceListing.buyer') : t('marketplaceListing.seller')}
              </h3>
              <div className="flex items-center gap-3">
                {listing.seller.profilePicture ? (
                  <img
                    src={listing.seller.profilePicture}
                    alt={listing.seller.displayName}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                ) : (
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${listing.listingType?.toUpperCase() === 'WTB' ? 'bg-green-600' : 'bg-blue-600'}`}>
                    {listing.seller.displayName.charAt(0).toUpperCase()}
                  </div>
                )}
                <div>
                  <div className="font-medium text-gray-900">
                    {listing.seller.displayName}
                  </div>
                  {/* Rating Display */}
                  <div className="flex items-center gap-2 mb-1">
                    <span className="flex items-center">
                      {renderStars(listing.seller.averageRating || 0)}
                      <span className="ml-2 text-sm font-medium text-gray-700">
                        {(listing.seller.averageRating || 0).toFixed(1)}
                      </span>
                    </span>
                    <span className="text-xs text-gray-500">
                      ({listing.seller.totalReviews || 0})
                    </span>
                  </div>
                  <Link
                    to={`/users/${listing.seller.id}/reviews`}
                    className="text-xs text-blue-600 hover:underline block"
                  >
                    {t('marketplaceListing.viewReliability')}
                  </Link>
                  <div className="text-xs text-gray-500 mt-1">
                    {t('marketplaceListing.listedOn', { date: new Date(listing.createdAt).toLocaleDateString() })}
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            {(() => {
              // Check if current user is the seller
              const isSeller = currentUser?.id === listing.sellerId;
              // const isAdmin = currentUser?.role === 'admin';

              if (listing.status !== 'ACTIVE') {
                return (
                  <div className="w-full bg-gray-200 text-gray-600 py-4 rounded-lg text-center font-bold text-lg">
                    {t('marketplaceListing.closed') || 'Listing Closed'}
                    {listing.closingPrice && (
                      <div className="text-sm font-normal mt-1">
                        Sold for: ฿{listing.closingPrice.toLocaleString()} (Community Reported)
                      </div>
                    )}
                  </div>
                );
              }

              if (isSeller) {
                return (
                  <div className="space-y-3">
                    <button
                      onClick={() => setIsCloseModalOpen(true)}
                      className="w-full bg-blue-600 text-white py-4 rounded-lg hover:bg-blue-700 transition font-bold text-lg"
                    >
                      {t('marketplaceListing.closeListing') || 'Close Listing'}
                    </button>
                    <button
                      onClick={() => navigate(`/marketplace/listing/${listing.id}/edit`)}
                      className="w-full bg-gray-100 text-gray-700 py-4 rounded-lg hover:bg-gray-200 transition font-bold text-lg"
                    >
                      {t('marketplaceListing.editListing')}
                    </button>
                    <button
                      onClick={handleCancelListing}
                      className="w-full bg-red-100 text-red-700 py-4 rounded-lg hover:bg-red-200 transition font-bold text-lg"
                    >
                      {t('marketplaceListing.cancelListing') || 'Cancel Listing'}
                    </button>
                  </div>
                );
              } else {
                // Buyer View
                return (
                  <div className="space-y-3">
                    {listing.externalLink && (
                      <a
                        href={listing.externalLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block w-full bg-blue-600 text-white py-4 rounded-lg hover:bg-blue-700 transition font-bold text-lg text-center"
                      >
                        {t('marketplaceListing.viewOnFacebook') || 'View Facebook Post'}
                      </a>
                    )}

                    {listing.seller.discordId && (
                      <a
                        href={`https://discord.com/users/${listing.seller.discordId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block w-full bg-indigo-600 text-white py-4 rounded-lg hover:bg-indigo-700 transition font-bold text-lg text-center flex items-center justify-center gap-2"
                      >
                        <span>💬</span> {t('marketplaceListing.messageOnDiscord') || 'Message on Discord'}
                      </a>
                    )}

                    {!listing.externalLink && !listing.seller.discordId && (
                      <div className="text-center p-4 bg-gray-100 rounded-lg text-gray-500">
                        {t('marketplaceListing.contactViaCommunity') || 'Contact seller via authorized community channels.'}
                      </div>
                    )}
                  </div>
                );
              }
            })()}
          </div>
        </div>
      </div>

      <CloseListingModal
        isOpen={isCloseModalOpen}
        onClose={() => setIsCloseModalOpen(false)}
        onConfirm={handleCloseListing}
        listingType={listing?.listingType || 'WTS'}
        maxQuantity={listing.quantity}
        defaultPrice={listing.price}
      />

      {/* Change Log - Admin Only */}
      {currentUser && currentUser.role === 'admin' && (
        <div className="mt-8">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold mb-4">{t('marketplaceListing.changeLog.title')}</h2>
            {changeLogs.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('marketplaceListing.changeLog.dateTime')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('marketplaceListing.changeLog.changeType')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('marketplaceListing.changeLog.oldValue')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('marketplaceListing.changeLog.newValue')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('marketplaceListing.changeLog.changedBy')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {changeLogs.map((change) => {
                      // Helper function to render change values based on type
                      const renderChangeValue = (value: string | null, type: string, isOldValue: boolean) => {
                        if (!value) return '-';

                        if (type === 'IMAGES_UPDATE') {
                          try {
                            const images = JSON.parse(value);
                            return t('marketplaceListing.changeLog.images', { count: images.length });
                          } catch {
                            return value;
                          }
                        }

                        if (type === 'IMAGES_DELETED' && isOldValue) {
                          // Show deleted image URLs for audit
                          try {
                            const deletedUrls = JSON.parse(value);
                            return (
                              <div className="max-w-md">
                                <div className="font-semibold text-red-600 mb-1">
                                  {t('marketplaceListing.changeLog.imagesDeleted', { count: deletedUrls.length })}
                                </div>
                                <ul className="text-xs space-y-1">
                                  {deletedUrls.map((url: string, idx: number) => (
                                    <li key={idx} className="truncate" title={url}>
                                      <a
                                        href={url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-600 hover:underline"
                                      >
                                        {url.split('/').pop() || url}
                                      </a>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            );
                          } catch {
                            return value;
                          }
                        }

                        return value;
                      };

                      return (
                        <tr key={change.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            {new Date(change.createdAt).toLocaleString()}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                            {change.changeType.replace(/_/g, ' ')}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {renderChangeValue(change.oldValue, change.changeType, true)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {renderChangeValue(change.newValue, change.changeType, false)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                            #{change.changedBy}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p className="text-lg">{t('marketplaceListing.changeLog.noChanges')}</p>
                <p className="text-sm mt-2">{t('marketplaceListing.changeLog.desc')}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ListingDetailPage;
